import { Button } from "@component/button";
import {
    ArrowRightIcon,
    ArrowLeftIcon,
    UserIcon,
    BriefcaseIcon,
    BuildingIcon,
} from "lucide-react";
import { CreateAccountData } from "@container/employer-forms/employer-register-form";
import { CompanyProfileData } from "@container/employer-forms/company-profile-form";
import { Badge } from "@component//badge";
import { useNavigate } from "react-router-dom";

interface ReviewFormProps {
    accountData: CreateAccountData & {
        hiringCount: string;
        employeeTypes: string[];
        organizationType: string;
        sub?: string
    };
    companyData: CompanyProfileData;
    onEditAccountData: () => void;
    onEditEmployerDetails: () => void;
    onEditCompanyProfile: () => void;
    onSubmit: () => void;
    className?: string;
}

export default function ReviewForm({
    accountData,
    companyData,
    onEditAccountData,
    onEditEmployerDetails,
    onEditCompanyProfile,
    onSubmit,
    className,
}: ReviewFormProps) {
    const navigate = useNavigate();

    const handleSubmit = () => {
        // Call the onSubmit prop directly - all data is already in the parent
        onSubmit();
    };

    return (
        <div
            className={`space-y-6 ${className}`}
            data-pol-id="l43ell"
            data-pol-file-name="review-form"
            data-pol-file-type="component"
        >
            <div
                className="space-y-4"
                data-pol-id="mee3nt"
                data-pol-file-name="review-form"
                data-pol-file-type="component"
            >
                <div
                    className="rounded-md border p-4"
                    data-pol-id="uy0lk1"
                    data-pol-file-name="review-form"
                    data-pol-file-type="component"
                >
                    <div
                        className="flex items-center justify-between mb-4"
                        data-pol-id="s6uujx"
                        data-pol-file-name="review-form"
                        data-pol-file-type="component"
                    >
                        <div
                            className="flex items-center gap-2"
                            data-pol-id="h65w9u"
                            data-pol-file-name="review-form"
                            data-pol-file-type="component"
                        >
                            <UserIcon
                                className="h-5 w-5 text-blue-600"
                                data-pol-id="dxshuh"
                                data-pol-file-name="review-form"
                                data-pol-file-type="component"
                            />

                            <h3
                                className="text-lg font-medium"
                                data-pol-id="978616"
                                data-pol-file-name="review-form"
                                data-pol-file-type="component"
                            >
                                Account Information
                            </h3>
                        </div>
                    </div>
                    <dl
                        className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2"
                        data-pol-id="fg7nhs"
                        data-pol-file-name="review-form"
                        data-pol-file-type="component"
                    >
                        <div
                            data-pol-id="aevsgg"
                            data-pol-file-name="review-form"
                            data-pol-file-type="component"
                        >
                            <dt
                                className="text-sm text-gray-900 font-bold"
                                data-pol-id="2c1owa"
                                data-pol-file-name="review-form"
                                data-pol-file-type="component"
                            >
                                First Name
                            </dt>
                            <dd
                                className="mt-1 text-sm text-gray-900"
                                data-pol-id="ts2ez7"
                                data-pol-file-name="review-form"
                                data-pol-file-type="component"
                            >
                                {accountData.firstName}
                            </dd>
                        </div>
                        <div
                            data-pol-id="aevsgg-last"
                            data-pol-file-name="review-form"
                            data-pol-file-type="component"
                        >
                            <dt
                                className="text-sm text-gray-900 font-bold"
                                data-pol-id="2c1owa-last"
                                data-pol-file-name="review-form"
                                data-pol-file-type="component"
                            >
                                Last Name
                            </dt>
                            <dd
                                className="mt-1 text-sm text-gray-900"
                                data-pol-id="ts2ez7-last"
                                data-pol-file-name="review-form"
                                data-pol-file-type="component"
                            >
                                {accountData.lastName}
                            </dd>
                        </div>
                        <div
                            data-pol-id="4iwq3t"
                            data-pol-file-name="review-form"
                            data-pol-file-type="component"
                        >
                            <dt
                                className="text-sm text-gray-900 font-bold"
                                data-pol-id="wjh673"
                                data-pol-file-name="review-form"
                                data-pol-file-type="component"
                            >
                                Work Email
                            </dt>
                            <dd
                                className="mt-1 text-sm text-gray-900"
                                data-pol-id="my9gne"
                                data-pol-file-name="review-form"
                                data-pol-file-type="component"
                            >
                                {accountData.email}
                            </dd>
                        </div>
                        {accountData.sub === "" && <div
                            data-pol-id="u4i9da"
                            data-pol-file-name="review-form"
                            data-pol-file-type="component"
                        >
                            <dt
                                className="text-sm text-gray-900 font-bold"
                                data-pol-id="p3d00v"
                                data-pol-file-name="review-form"
                                data-pol-file-type="component"
                            >
                                Password
                            </dt>
                            <dd
                                className="mt-1 text-sm text-gray-900"
                                data-pol-id="u932oq"
                                data-pol-file-name="review-form"
                                data-pol-file-type="component"
                            >
                                ••••••••
                            </dd>
                        </div>}
                    </dl>
                </div>

                <div
                    className="rounded-md border p-4"
                    data-pol-id="97kioy"
                    data-pol-file-name="review-form"
                    data-pol-file-type="component"
                >
                    <div
                        className="flex items-center justify-between mb-4"
                        data-pol-id="ligx9y"
                        data-pol-file-name="review-form"
                        data-pol-file-type="component"
                    >
                        <div
                            className="flex items-center gap-2"
                            data-pol-id="vtzmrt"
                            data-pol-file-name="review-form"
                            data-pol-file-type="component"
                        >
                            <BriefcaseIcon
                                className="h-5 w-5 text-blue-600"
                                data-pol-id="a3cd5o"
                                data-pol-file-name="review-form"
                                data-pol-file-type="component"
                            />

                            <h3
                                className="text-lg font-medium"
                                data-pol-id="hprlt6"
                                data-pol-file-name="review-form"
                                data-pol-file-type="component"
                            >
                                Employer Details
                            </h3>
                        </div>
                    </div>
                    <dl
                        className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2"
                        data-pol-id="guc6d2"
                        data-pol-file-name="review-form"
                        data-pol-file-type="component"
                    >
                        <div
                            data-pol-id="o4gve3"
                            data-pol-file-name="review-form"
                            data-pol-file-type="component"
                        >
                            <dt
                                className="text-sm text-gray-900 font-bold"
                                data-pol-id="a6q564"
                                data-pol-file-name="review-form"
                                data-pol-file-type="component"
                            >
                                Hiring Count
                            </dt>
                            <dd
                                className="mt-1 text-sm text-gray-900"
                                data-pol-id="8o9ima"
                                data-pol-file-name="review-form"
                                data-pol-file-type="component"
                            >
                                {accountData.hiringCount}
                            </dd>
                        </div>
                        <div
                            data-pol-id="dxu865"
                            data-pol-file-name="review-form"
                            data-pol-file-type="component"
                        >
                            <dt
                                className="text-sm text-gray-900 font-bold"
                                data-pol-id="hpji52"
                                data-pol-file-name="review-form"
                                data-pol-file-type="component"
                            >
                                Employee Types
                            </dt>
                            <dd
                                className="mt-1 flex flex-wrap gap-2"
                                data-pol-id="xi7scj"
                                data-pol-file-name="review-form"
                                data-pol-file-type="component"
                            >
                                {accountData.employeeTypes.map((type, index) => (
                                    <Badge
                                        key={index}
                                        variant="secondary"
                                        className="text-xs font-normal"
                                        data-pol-id={`ys5zel_${index}`}
                                        data-pol-file-name="review-form"
                                        data-pol-file-type="component"
                                    >
                                        {type}
                                    </Badge>
                                ))}
                            </dd>
                        </div>
                        <div
                            className="sm:col-span-2"
                            data-pol-id="ojk99s"
                            data-pol-file-name="review-form"
                            data-pol-file-type="component"
                        >
                            <dt
                                className="text-sm text-gray-900 font-bold"
                                data-pol-id="vkotoc"
                                data-pol-file-name="review-form"
                                data-pol-file-type="component"
                            >
                                Organization Type
                            </dt>
                            <dd
                                className="mt-1 text-sm text-gray-900"
                                data-pol-id="xzgbb0"
                                data-pol-file-name="review-form"
                                data-pol-file-type="component"
                            >
                                {accountData.organizationType}
                            </dd>
                        </div>
                    </dl>
                </div>

                <div
                    className="rounded-md border p-4"
                    data-pol-id="7lried"
                    data-pol-file-name="review-form"
                    data-pol-file-type="component"
                >
                    <div
                        className="flex items-center justify-between mb-4"
                        data-pol-id="2vvl7e"
                        data-pol-file-name="review-form"
                        data-pol-file-type="component"
                    >
                        <div
                            className="flex items-center gap-2"
                            data-pol-id="olrzh4"
                            data-pol-file-name="review-form"
                            data-pol-file-type="component"
                        >
                            <BuildingIcon
                                className="h-5 w-5 text-blue-600"
                                data-pol-id="yrkdic"
                                data-pol-file-name="review-form"
                                data-pol-file-type="component"
                            />

                            <h3
                                className="text-lg font-medium"
                                data-pol-id="ngz3q1"
                                data-pol-file-name="review-form"
                                data-pol-file-type="component"
                            >
                                Company Profile
                            </h3>
                        </div>
                    </div>
                    <dl
                        className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2"
                        data-pol-id="4vo29p"
                        data-pol-file-name="review-form"
                        data-pol-file-type="component"
                    >
                        <div
                            data-pol-id="qnhf5e"
                            data-pol-file-name="review-form"
                            data-pol-file-type="component"
                        >
                            <dt
                                className="text-sm text-gray-900 font-bold"
                                data-pol-id="ey0am3"
                                data-pol-file-name="review-form"
                                data-pol-file-type="component"
                            >
                                Company Name
                            </dt>
                            <dd
                                className="mt-1 text-sm text-gray-900"
                                data-pol-id="f5ph81"
                                data-pol-file-name="review-form"
                                data-pol-file-type="component"
                            >
                                {companyData.companyName}
                            </dd>
                        </div>
                        <div
                            data-pol-id="kl2zdl"
                            data-pol-file-name="review-form"
                            data-pol-file-type="component"
                        >
                            <dt
                                className="text-sm text-gray-900 font-bold"
                                data-pol-id="f72xeg"
                                data-pol-file-name="review-form"
                                data-pol-file-type="component"
                            >
                                LinkedIn Profile
                            </dt>
                            <dd
                                className="mt-1 text-sm text-gray-900 break-words"
                            >
                                {companyData.companyLinkedin || "Not provided"}
                            </dd>
                        </div>
                        <div
                            className="sm:col-span-2"
                            data-pol-id="ul2xkp"
                            data-pol-file-name="review-form"
                            data-pol-file-type="component"
                        >
                            <dt
                                className="text-sm text-gray-900 font-bold"
                                data-pol-id="6y6cib"
                                data-pol-file-name="review-form"
                                data-pol-file-type="component"
                            >
                                Company Description
                            </dt>
                            <dd
                                className="mt-1 text-sm text-gray-900"
                                data-pol-id="moat69"
                                data-pol-file-name="review-form"
                                data-pol-file-type="component"
                            >
                                {companyData.companyDescription}
                            </dd>
                        </div>
                        {companyData.companyLogo && (
                            <div
                                data-pol-id="ww556u"
                                data-pol-file-name="review-form"
                                data-pol-file-type="component"
                            >
                                <dt
                                    className="text-sm text-gray-900 font-bold"
                                    data-pol-id="u700tn"
                                    data-pol-file-name="review-form"
                                    data-pol-file-type="component"
                                >
                                    Company Logo
                                </dt>
                                <dd
                                    className="mt-1"
                                    data-pol-id="iuhqx0"
                                    data-pol-file-name="review-form"
                                    data-pol-file-type="component"
                                >
                                    <div
                                        className="h-16 w-16 rounded-md border overflow-hidden"
                                        data-pol-id="e6xa2n"
                                        data-pol-file-name="review-form"
                                        data-pol-file-type="component"
                                    >
                                        <img
                                            src={URL.createObjectURL(companyData.companyLogo)}
                                            alt="Company Logo"
                                            className="h-full w-full object-cover"
                                            data-pol-id="lt5h6a"
                                            data-pol-file-name="review-form"
                                            data-pol-file-type="component"
                                        />
                                    </div>
                                </dd>
                            </div>
                        )}
                    </dl>
                </div>
            </div>

            <div
                className="flex flex-col sm:flex-row gap-3"
                data-pol-id="uv4z7l"
                data-pol-file-name="review-form"
                data-pol-file-type="component"
            >
                <Button
                    onClick={onEditEmployerDetails}
                    variant="outline"
                    className="sm:flex-1"
                    data-pol-id="frjwg1"
                    data-pol-file-name="review-form"
                    data-pol-file-type="component"
                >
                    <ArrowLeftIcon
                        className="mr-2 h-4 w-4"
                        data-pol-id="hxpump"
                        data-pol-file-name="review-form"
                        data-pol-file-type="component"
                    />
                    Back to Edit
                </Button>
                <Button
                    onClick={handleSubmit}
                    className="w-full sm:flex-1 bg-blue-700 hover:bg-blue-800 text-white"
                    data-pol-id="fbpa47"
                    data-pol-file-name="review-form"
                    data-pol-file-type="component"
                >
                    Submit
                    <ArrowRightIcon
                        className="ml-2 h-4 w-4"
                        data-pol-id="62gqkw"
                        data-pol-file-name="review-form"
                        data-pol-file-type="component"
                    />
                </Button>
            </div>
        </div>
    );
}
